/**
 * Rounding Strike Guard — dashboard port of the toolkit module
 * (homerunner-toolkit `includes/RoundingStrikeGuard/Core.php` +
 * `assets/js/rounding-strike-guard.js`, toolkit PR #655, release 3.234.0;
 * mapped to #1064 in the 2026-08-27 parity audit).
 *
 * The booking widget's card and floating bar, and the explorer's two card
 * price renderers (GridQuotePrice / TemplateExplorer — a deliberate
 * duplicate pair per their own docblocks), draw a crossed-out "was" price
 * whenever a quote's rateplan carries any `discount`. On rates priced in
 * cents (e.g. $6,833.33/night), Central's sub-dollar rounding reconciliation
 * itself comes through as a `discount`, so the widgets draw a false strike
 * over a price that was never really discounted. Real discounts — coupons,
 * weekly/monthly rates — produce a larger per-night gap and should keep
 * their strike.
 *
 * This mirrors the toolkit's comparison exactly: `Core.php`'s
 * `rounding-strike-guard.js` removes the `<del>` when the struck and active
 * PER-NIGHT prices differ by less than the threshold (`was - now <
 * THRESHOLD`, strict) — so a gap of exactly $1.00/night keeps its strike,
 * and anything below it loses one. The toolkit ships the threshold as a
 * dev-only WP filter (`hrtk_rounding_strike_guard_threshold`), not an
 * operator-facing setting — `RoundingStrikeGuard` has no `Settings.php`,
 * unlike modules such as `FeeLineSeparator` — so there is no widget config
 * key to mirror; the $1.00/night default below matches
 * `Core::DEFAULT_THRESHOLD` verbatim.
 *
 * `rateplan.discount` carries a PMS-dependent sign (Guesty positive;
 * Hostaway / OwnerRez / Hostfully-v3 negative), so callers pass the raw
 * signed value here and this takes the magnitude itself — same convention
 * as `BookingForm.tsx`'s `nightlyRate` and `grid-quote-price.tsx`'s
 * `hasDiscount`.
 */

/** Per-night dollar gap below which a discount is rounding noise, not real — matches toolkit `Core::DEFAULT_THRESHOLD`. */
export const ROUNDING_STRIKE_GUARD_THRESHOLD_PER_NIGHT = 1.0

/**
 * Whether a rateplan's discount is large enough, per night, to justify
 * showing a struck-through "was" price alongside the active one.
 *
 * `discountAmount` is the rateplan's total discount for the stay (any
 * sign — the magnitude is taken here); `nights` is the stay length. A
 * non-positive `nights` (never reached with a real quote) falls back to
 * treating the whole discount as the per-night gap, so the guard still
 * degrades toward "show the strike" rather than dividing by zero.
 */
export function isRoundingStrikeWorthShowing(
  discountAmount: number,
  nights: number,
  thresholdPerNight: number = ROUNDING_STRIKE_GUARD_THRESHOLD_PER_NIGHT,
): boolean {
  const magnitude = Math.abs(discountAmount)
  const gapPerNight = nights > 0 ? magnitude / nights : magnitude
  return gapPerNight >= thresholdPerNight
}

/**
 * #1691 — toolkit original-total module (`includes/Modules/OriginalTotal.php`).
 * The pre-discount total to strike beside the discounted total, or null when
 * there is nothing honest to strike (no discount, or a rounding-only gap).
 *
 * `displayedTotal` is the post-discount total exactly as the surface prints it
 * (after tax or pre-tax); the original is that plus the discount magnitude, so
 * the booking widget Total row and the explorer card line print the same
 * figure for the same quote. Reuses `isRoundingStrikeWorthShowing`; there is
 * no second threshold. `discount` takes any PMS sign.
 */
export function originalTotalOf(displayedTotal: number, discount: number, nights: number): number | null {
  const magnitude = Math.abs(discount || 0)
  if (magnitude <= 0 || !isRoundingStrikeWorthShowing(magnitude, nights)) return null
  return displayedTotal + magnitude
}
